import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, FileText, CalendarDays, Folder, Sparkles, Activity, LogOut, Trash2, Pencil,
  Plus, Search, X, Send, BookOpen, ShoppingBag,
} from "lucide-react";
import { api, errMsg, LOGO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inp = "w-full border border-[#dcd5ce] bg-white px-3 py-2 text-sm outline-none focus:border-[#c5a059]";
const HIST_FIELDS = [
  ["datos_personales", "Datos personales"], ["motivo_consulta", "Motivo de consulta"], ["antecedentes", "Antecedentes"],
  ["evaluaciones", "Evaluaciones"], ["observaciones", "Observaciones"], ["objetivos", "Objetivos"],
  ["intervenciones", "Intervenciones"], ["evolucion", "Evolución"], ["seguimiento", "Seguimiento"],
];
const TIPOS_INFORME = [
  "Informe psicopedagógico", "Informe evolutivo", "Informe para padres", "Informe para escuela",
  "Informe para institución", "Informe para médico", "Informe psicopedagógico forense",
];
const SECTIONS = [
  ["dashboard", "Panel", Activity], ["pacientes", "Pacientes", Users], ["informes", "Informes IA", FileText],
  ["agenda", "Agenda", CalendarDays], ["biblioteca", "Biblioteca", Folder], ["material", "Material IA", Sparkles],
  ["tienda", "Tienda", ShoppingBag], ["actividad", "Actividad", BookOpen],
];

export default function Admin() {
  const { user, logout } = useAuth();
  const [sec, setSec] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [hist, setHist] = useState({});
  const [form, setForm] = useState(null);
  const [reports, setReports] = useState([]);
  const [aiTipo, setAiTipo] = useState(TIPOS_INFORME[0]);
  const [aiInd, setAiInd] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [editReport, setEditReport] = useState(null);
  const [tl, setTl] = useState([]);
  const [tlForm, setTlForm] = useState({ fecha: "", tipo: "sesion", objetivos: "", avances: "", dificultades: "", proximos_objetivos: "", logro: "", visible_paciente: true });
  const [consents, setConsents] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotForm, setSlotForm] = useState({ fecha: "", hora: "", cupos: 1 });
  const [appts, setAppts] = useState([]);
  const [lib, setLib] = useState([]);
  const [libForm, setLibForm] = useState({ titulo: "", descripcion: "", categoria: "material", url: "", contenido: "" });
  const [matForm, setMatForm] = useState({ area: "", edad: "", objetivo: "" });
  const [matOut, setMatOut] = useState("");
  const [acts, setActs] = useState([]);
  const [products, setProducts] = useState([]);
  const [prodForm, setProdForm] = useState(null);
  const [orders, setOrders] = useState([]);
  const [delTarget, setDelTarget] = useState(null);
  const [delStep, setDelStep] = useState(0);

  const loadPatients = (query = "") => api.get("/patients", { params: { q: query } }).then((r) => setPatients(r.data));

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(errMsg(e)));
    loadPatients();
    api.get("/slots").then((r) => setSlots(r.data));
    api.get("/appointments").then((r) => setAppts(r.data));
    api.get("/library").then((r) => setLib(r.data));
    api.get("/admin/activity").then((r) => setActs(r.data));
    api.get("/products", { params: { all: true } }).then((r) => setProducts(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
  }, []);

  const openPatient = async (p) => {
    setSel(p); setHist(p.historia || {});
    const [r, t, c] = await Promise.all([
      api.get("/reports", { params: { patient_id: p.id } }),
      api.get("/timeline", { params: { patient_id: p.id } }),
      api.get(`/patients/${p.id}/consents`),
    ]);
    setReports(r.data); setTl(t.data); setConsents(c.data);
  };

  const savePatient = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/patients/${form.id}`, form);
      else await api.post("/patients", form);
      toast.success("Paciente guardado");
      setForm(null); loadPatients(q);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/patients/${delTarget.id}`, { params: { confirm: "ELIMINAR" } });
      toast.success("Paciente eliminado con todos sus datos");
      setDelTarget(null); setDelStep(0); setSel(null); loadPatients(q);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveHist = async () => {
    try { await api.put(`/patients/${sel.id}/historia`, hist); toast.success("Historia clínica guardada"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const generarIA = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/report", { patient_id: sel.id, tipo: aiTipo, indicaciones: aiInd });
      setReports((r) => [data, ...r]); setEditReport(data);
      toast.success("Informe generado. Revisalo y editalo antes de aprobar.");
    } catch (e) { toast.error(errMsg(e)); } finally { setAiBusy(false); }
  };

  const saveReport = async (patch) => {
    try {
      const { data } = await api.put(`/reports/${editReport.id}`, patch);
      setReports((rs) => rs.map((r) => (r.id === data.id ? data : r)));
      setEditReport(data); toast.success("Informe actualizado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openPdf = (path) => api.get(path, { responseType: "blob" })
    .then((r) => window.open(URL.createObjectURL(r.data), "_blank")).catch(() => toast.error("Error al generar PDF"));

  const addTl = async () => {
    try {
      const { data } = await api.post("/timeline", { ...tlForm, patient_id: sel.id });
      setTl((x) => [data, ...x]); toast.success("Registro agregado");
      setTlForm({ ...tlForm, objetivos: "", avances: "", dificultades: "", proximos_objetivos: "", logro: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addSlot = async () => {
    try { const { data } = await api.post("/slots", slotForm); setSlots((s) => [...s, data]); toast.success("Disponibilidad agregada"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const setAppt = async (id, estado) => {
    try {
      const { data } = await api.put(`/appointments/${id}`, { estado });
      setAppts((a) => a.map((x) => (x.id === id ? { ...x, estado } : x)));
      window.open(data.whatsapp_url, "_blank");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addLib = async () => {
    try { const { data } = await api.post("/library", libForm); setLib((l) => [data, ...l]); setLibForm({ titulo: "", descripcion: "", categoria: "material", url: "", contenido: "" }); toast.success("Material agregado"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const shareLib = async (lid, pid) => {
    try { await api.post(`/library/${lid}/share`, { patient_id: pid, share: true }); toast.success("Compartido con el paciente"); api.get("/library").then((r) => setLib(r.data)); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const genMaterial = async () => {
    setAiBusy(true);
    try { const { data } = await api.post("/ai/material", matForm); setMatOut(data.contenido); }
    catch (e) { toast.error(errMsg(e)); } finally { setAiBusy(false); }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    try {
      const body = { ...prodForm, precio: Number(prodForm.precio) || 0 };
      if (prodForm.id) {
        const { data } = await api.put(`/products/${prodForm.id}`, body);
        setProducts((ps) => ps.map((p) => (p.id === data.id ? data : p)));
      } else {
        const { data } = await api.post("/products", body);
        setProducts((ps) => [data, ...ps]);
      }
      setProdForm(null);
      toast.success("Producto guardado");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const delProduct = async (id) => {
    try { await api.delete(`/products/${id}`); setProducts((ps) => ps.filter((p) => p.id !== id)); toast.success("Producto eliminado"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const setOrder = async (id, estado) => {
    try {
      const { data } = await api.put(`/orders/${id}`, { estado });
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, estado } : o)));
      if (estado === "entregado") window.open(data.whatsapp_url, "_blank");
      toast.success("Venta actualizada");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#f9f8f6]">
      <header className="border-b border-[#dcd5ce] bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="Nilo" className="h-9 w-9 rounded-full" />
            <span><span className="block serif text-lg leading-none">Panel Profesional</span><span className="label-xs">Lic. {user?.nombre} {user?.apellido}</span></span>
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-xs text-[#5e5a56] hover:text-[#c5a059]"><LogOut size={14} /> Salir</button>
        </div>
      </header>

      <div className="mx-auto flex max-w-7xl gap-6 px-4 py-6">
        <aside className="hidden w-52 shrink-0 lg:block">
          {SECTIONS.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setSec(k)} data-testid={`admin-nav-${k}`}
              className={`mb-1 flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${sec === k ? "bg-white border-l-2 border-l-[#c5a059] text-[#2a2826]" : "text-[#5e5a56] hover:text-[#c5a059]"}`}>
              <Icon size={15} /> {l}
            </button>
          ))}
        </aside>

        <main className="min-w-0 flex-1">
          <div className="mb-4 flex gap-2 overflow-x-auto lg:hidden">
            {SECTIONS.map(([k, l]) => (
              <button key={k} onClick={() => setSec(k)} className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs ${sec === k ? "btn-gold" : "text-[#5e5a56]"}`}>{l}</button>
            ))}
          </div>

          {sec === "dashboard" && stats && (
            <div data-testid="admin-dashboard" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[["Pacientes", stats.pacientes], ["Informes", stats.informes], ["Turnos pendientes", stats.turnos_pendientes],
                ["Consentimientos pendientes", stats.consentimientos_pendientes], ["Materiales", stats.materiales],
                ["Productos", stats.productos], ["Ventas pendientes", stats.ventas_pendientes]].map(([k, v]) => (
                <div key={k} className="card-nilo p-6"><p className="label-xs">{k}</p><p className="mt-2 serif text-4xl">{v}</p></div>
              ))}
            </div>
          )}

          {sec === "pacientes" && (
            <div data-testid="admin-pacientes">
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <div className="flex flex-1 items-center gap-2 border border-[#dcd5ce] bg-white px-3">
                  <Search size={14} className="text-[#8b8480]" />
                  <input data-testid="patient-search" placeholder="Buscar por nombre o DNI" value={q}
                    onChange={(e) => { setQ(e.target.value); loadPatients(e.target.value); }} className="w-full py-2 text-sm outline-none" />
                </div>
                <button data-testid="patient-new" onClick={() => setForm({ dni: "", nombre: "", apellido: "", email: "", telefono: "", fecha_nacimiento: "", escuela: "", grado: "", tutor: "", notas: "" })}
                  className="btn-gold flex items-center gap-2 rounded-full px-5 py-2 text-xs uppercase tracking-[0.14em]"><Plus size={13} /> Nuevo paciente</button>
              </div>

              <div className="space-y-2">
                {patients.map((p) => (
                  <div key={p.id} data-testid={`patient-row-${p.dni}`} className="card-nilo flex flex-wrap items-center justify-between gap-3 p-4">
                    <button onClick={() => openPatient(p)} className="text-left">
                      <p className="serif text-xl">{p.apellido}, {p.nombre}</p>
                      <p className="label-xs mt-0.5">DNI {p.dni} {p.escuela ? `· ${p.escuela}` : ""}</p>
                    </button>
                    <div className="flex gap-2">
                      <button data-testid={`patient-open-${p.dni}`} onClick={() => openPatient(p)} className="btn-outline-ink rounded-full px-4 py-1.5 text-xs uppercase">Abrir</button>
                      <button data-testid={`patient-edit-${p.dni}`} onClick={() => setForm(p)} className="btn-outline-ink flex items-center gap-1 rounded-full px-4 py-1.5 text-xs uppercase"><Pencil size={12} /> Editar</button>
                      <button data-testid={`patient-delete-${p.dni}`} onClick={() => { setDelTarget(p); setDelStep(1); }} className="flex items-center gap-1 rounded-full border border-[#993333] px-4 py-1.5 text-xs uppercase text-[#993333]"><Trash2 size={12} /> Eliminar</button>
                    </div>
                  </div>
                ))}
                {patients.length === 0 && <p className="card-nilo p-8 text-sm text-[#5e5a56]">Sin pacientes registrados.</p>}
              </div>

              {sel && (
                <div data-testid="patient-detail" className="mt-8 card-nilo p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="label-xs">Ficha del paciente</p>
                      <h2 className="serif text-3xl">{sel.apellido}, {sel.nombre}</h2>
                    </div>
                    <button onClick={() => setSel(null)}><X size={18} /></button>
                  </div>

                  <p className="label-xs mt-8">Historia clínica digital</p>
                  <div className="mt-3 grid gap-4 md:grid-cols-2">
                    {HIST_FIELDS.map(([k, l]) => (
                      <div key={k}>
                        <label className="text-xs text-[#5e5a56]">{l}</label>
                        <textarea data-testid={`hist-${k}`} rows={3} className={inp} value={hist[k] || ""} onChange={(e) => setHist({ ...hist, [k]: e.target.value })} />
                      </div>
                    ))}
                  </div>
                  <button data-testid="hist-save" onClick={saveHist} className="mt-4 btn-gold rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.14em]">Guardar historia clínica</button>

                  <p className="label-xs mt-10">Informes con inteligencia artificial</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-[1fr_1.4fr_auto]">
                    <select data-testid="ai-tipo" className={inp} value={aiTipo} onChange={(e) => setAiTipo(e.target.value)}>
                      {TIPOS_INFORME.map((t) => <option key={t}>{t}</option>)}
                    </select>
                    <input data-testid="ai-indicaciones" className={inp} placeholder="Indicaciones para la redacción" value={aiInd} onChange={(e) => setAiInd(e.target.value)} />
                    <button data-testid="ai-generar" disabled={aiBusy} onClick={generarIA} className="btn-gold whitespace-nowrap rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.14em]">
                      {aiBusy ? "Generando..." : "Generar con IA"}
                    </button>
                  </div>

                  <div className="mt-4 space-y-2">
                    {reports.map((r) => (
                      <div key={r.id} className="border border-[#dcd5ce] p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div><p className="serif text-lg">{r.titulo}</p><p className="label-xs">{r.estado}{r.shared ? " · compartido" : ""}</p></div>
                          <div className="flex gap-2">
                            <button data-testid={`report-edit-${r.id}`} onClick={() => setEditReport(r)} className="btn-outline-ink rounded-full px-4 py-1.5 text-xs uppercase">Revisar</button>
                            <button data-testid={`report-pdf-${r.id}`} onClick={() => openPdf(`/reports/${r.id}/pdf`)} className="btn-outline-ink rounded-full px-4 py-1.5 text-xs uppercase">PDF</button>
                            <button onClick={async () => { await api.delete(`/reports/${r.id}`); setReports((x) => x.filter((y) => y.id !== r.id)); }} className="rounded-full border border-[#993333] px-4 py-1.5 text-xs uppercase text-[#993333]">Eliminar</button>
                          </div>
                        </div>
                        {editReport?.id === r.id && (
                          <div className="mt-4" data-testid="report-editor">
                            <textarea rows={16} className={inp} value={editReport.contenido} onChange={(e) => setEditReport({ ...editReport, contenido: e.target.value })} />
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button data-testid="report-save" onClick={() => saveReport({ contenido: editReport.contenido })} className="btn-gold rounded-full px-5 py-2 text-xs uppercase">Guardar edición</button>
                              <button data-testid="report-approve" onClick={() => saveReport({ estado: "aprobado" })} className="btn-outline-ink rounded-full px-5 py-2 text-xs uppercase">Aprobar</button>
                              <button data-testid="report-share" onClick={() => saveReport({ shared: true, estado: "compartido" })} className="btn-outline-ink flex items-center gap-1 rounded-full px-5 py-2 text-xs uppercase"><Send size={12} /> Compartir con paciente</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <p className="label-xs mt-10">Seguimiento y pasaporte de aprendizaje</p>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <input data-testid="tl-fecha" type="date" className={inp} value={tlForm.fecha} onChange={(e) => setTlForm({ ...tlForm, fecha: e.target.value })} />
                    <input data-testid="tl-objetivos" placeholder="Objetivos" className={inp} value={tlForm.objetivos} onChange={(e) => setTlForm({ ...tlForm, objetivos: e.target.value })} />
                    <input data-testid="tl-avances" placeholder="Avances" className={inp} value={tlForm.avances} onChange={(e) => setTlForm({ ...tlForm, avances: e.target.value })} />
                    <input data-testid="tl-dificultades" placeholder="Dificultades" className={inp} value={tlForm.dificultades} onChange={(e) => setTlForm({ ...tlForm, dificultades: e.target.value })} />
                    <input data-testid="tl-proximos" placeholder="Próximos objetivos" className={inp} value={tlForm.proximos_objetivos} onChange={(e) => setTlForm({ ...tlForm, proximos_objetivos: e.target.value })} />
                    <input data-testid="tl-logro" placeholder="Logro para el pasaporte" className={inp} value={tlForm.logro} onChange={(e) => setTlForm({ ...tlForm, logro: e.target.value })} />
                  </div>
                  <label className="mt-3 flex items-center gap-2 text-xs text-[#5e5a56]">
                    <input type="checkbox" data-testid="tl-visible" checked={tlForm.visible_paciente} onChange={(e) => setTlForm({ ...tlForm, visible_paciente: e.target.checked })} />
                    Visible para el paciente
                  </label>
                  <button data-testid="tl-add" onClick={addTl} className="mt-3 btn-gold rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.14em]">Agregar registro</button>
                  <div className="mt-4 space-y-2">
                    {tl.map((t) => (
                      <div key={t.id} className="border-l-2 border-l-[#c5a059] bg-white px-4 py-3 text-sm">
                        <p className="label-xs">{t.fecha} {t.visible_paciente ? "· visible" : "· privado"}</p>
                        <p className="mt-1">{t.objetivos} {t.avances && `— ${t.avances}`}</p>
                      </div>
                    ))}
                  </div>

                  <p className="label-xs mt-10">Consentimientos</p>
                  <div className="mt-3 grid gap-2 md:grid-cols-2">
                    {consents.map((c) => (
                      <div key={c.id} className="flex items-center justify-between border border-[#dcd5ce] px-4 py-2 text-sm">
                        <span>{c.tipo}</span>
                        <span className={c.firmado ? "text-xs text-[#4a6b53]" : "text-xs text-[#993333]"}>{c.firmado ? "Firmado" : "Pendiente"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {sec === "informes" && (
            <div data-testid="admin-informes" className="card-nilo p-8">
              <p className="label-xs">Informes con IA</p>
              <h2 className="mt-2 text-3xl">Generación asistida</h2>
              <p className="mt-3 text-sm text-[#5e5a56]">
                Seleccioná un paciente en la sección Pacientes para generar, revisar, editar, aprobar y compartir informes.
                El flujo es: generación IA → revisión profesional → edición → aprobación → compartir.
              </p>
              <button onClick={() => setSec("pacientes")} className="mt-6 btn-gold rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.14em]">Ir a pacientes</button>
            </div>
          )}

          {sec === "agenda" && (
            <div data-testid="admin-agenda" className="grid gap-6 lg:grid-cols-2">
              <div className="card-nilo p-6">
                <p className="label-xs">Disponibilidad</p>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <input data-testid="slot-fecha" type="date" className={inp} value={slotForm.fecha} onChange={(e) => setSlotForm({ ...slotForm, fecha: e.target.value })} />
                  <input data-testid="slot-hora" type="time" className={inp} value={slotForm.hora} onChange={(e) => setSlotForm({ ...slotForm, hora: e.target.value })} />
                  <input data-testid="slot-cupos" type="number" min="1" className={inp} value={slotForm.cupos} onChange={(e) => setSlotForm({ ...slotForm, cupos: +e.target.value })} />
                </div>
                <button data-testid="slot-add" onClick={addSlot} className="mt-3 btn-gold rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.14em]">Agregar cupo</button>
                <div className="mt-4 space-y-2">
                  {slots.map((s) => (
                    <div key={s.id} className="flex items-center justify-between border border-[#dcd5ce] px-4 py-2 text-sm">
                      <span>{s.fecha} — {s.hora} hs ({s.cupos} cupos)</span>
                      <button onClick={async () => { await api.delete(`/slots/${s.id}`); setSlots((x) => x.filter((y) => y.id !== s.id)); }} className="text-xs text-[#993333]">Eliminar</button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card-nilo p-6">
                <p className="label-xs">Solicitudes de turno</p>
                <div className="mt-3 space-y-3">
                  {appts.length === 0 && <p className="text-sm text-[#5e5a56]">Sin solicitudes.</p>}
                  {appts.map((a) => (
                    <div key={a.id} className="border border-[#dcd5ce] p-4 text-sm">
                      <p className="serif text-lg">{a.nombre}</p>
                      <p className="label-xs mt-0.5">{a.fecha} — {a.hora} hs · {a.estado}</p>
                      {a.motivo && <p className="mt-1 text-[#5e5a56]">{a.motivo}</p>}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button data-testid={`appt-confirm-${a.id}`} onClick={() => setAppt(a.id, "confirmado")} className="btn-gold rounded-full px-4 py-1.5 text-xs uppercase">Confirmar</button>
                        <button onClick={() => setAppt(a.id, "reprogramado")} className="btn-outline-ink rounded-full px-4 py-1.5 text-xs uppercase">Reprogramar</button>
                        <button onClick={() => api.get(`/appointments/${a.id}/recordatorio`).then((r) => window.open(r.data.whatsapp_url, "_blank"))} className="btn-outline-ink rounded-full px-4 py-1.5 text-xs uppercase">Recordatorio</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {sec === "biblioteca" && (
            <div data-testid="admin-biblioteca" className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="card-nilo p-6">
                <p className="label-xs">Nuevo material</p>
                <div className="mt-3 space-y-3">
                  <input data-testid="lib-titulo" placeholder="Título" className={inp} value={libForm.titulo} onChange={(e) => setLibForm({ ...libForm, titulo: e.target.value })} />
                  <input data-testid="lib-desc" placeholder="Descripción" className={inp} value={libForm.descripcion} onChange={(e) => setLibForm({ ...libForm, descripcion: e.target.value })} />
                  <input data-testid="lib-cat" placeholder="Categoría" className={inp} value={libForm.categoria} onChange={(e) => setLibForm({ ...libForm, categoria: e.target.value })} />
                  <input data-testid="lib-url" placeholder="Enlace (opcional)" className={inp} value={libForm.url} onChange={(e) => setLibForm({ ...libForm, url: e.target.value })} />
                  <textarea data-testid="lib-contenido" rows={5} placeholder="Contenido / actividad" className={inp} value={libForm.contenido} onChange={(e) => setLibForm({ ...libForm, contenido: e.target.value })} />
                  <button data-testid="lib-add" onClick={addLib} className="btn-gold rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.14em]">Agregar a la biblioteca</button>
                </div>
              </div>
              <div className="space-y-3">
                {lib.map((m) => (
                  <div key={m.id} className="card-nilo p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div><p className="serif text-xl">{m.titulo}</p><p className="label-xs mt-0.5">{m.categoria} · compartido con {m.shared_with.length}</p></div>
                      <button onClick={async () => { await api.delete(`/library/${m.id}`); setLib((x) => x.filter((y) => y.id !== m.id)); }} className="text-xs text-[#993333]">Eliminar</button>
                    </div>
                    <select data-testid={`lib-share-${m.id}`} className={`${inp} mt-3`} defaultValue="" onChange={(e) => e.target.value && shareLib(m.id, e.target.value)}>
                      <option value="">Compartir con paciente...</option>
                      {patients.map((p) => <option key={p.id} value={p.id}>{p.apellido}, {p.nombre}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sec === "material" && (
            <div data-testid="admin-material" className="card-nilo p-8">
              <p className="label-xs">Generador de material con IA</p>
              <h2 className="mt-2 text-3xl">Actividades personalizadas</h2>
              <div className="mt-5 grid gap-3 md:grid-cols-3">
                <select data-testid="mat-area" className={inp} value={matForm.area} onChange={(e) => setMatForm({ ...matForm, area: e.target.value })}>
                  <option value="">Área de trabajo</option>
                  {["Atención", "Memoria", "Funciones ejecutivas", "Lectoescritura", "Ejercicios cognitivos", "Aprendizaje general"].map((a) => <option key={a}>{a}</option>)}
                </select>
                <input data-testid="mat-edad" placeholder="Edad o nivel" className={inp} value={matForm.edad} onChange={(e) => setMatForm({ ...matForm, edad: e.target.value })} />
                <input data-testid="mat-objetivo" placeholder="Objetivo terapéutico" className={inp} value={matForm.objetivo} onChange={(e) => setMatForm({ ...matForm, objetivo: e.target.value })} />
              </div>
              <button data-testid="mat-generar" disabled={aiBusy || !matForm.area} onClick={genMaterial} className="mt-4 btn-gold rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.14em]">
                {aiBusy ? "Generando..." : "Generar material"}
              </button>
              {matOut && (
                <>
                  <textarea data-testid="mat-output" rows={16} className={`${inp} mt-5`} value={matOut} onChange={(e) => setMatOut(e.target.value)} />
                  <button data-testid="mat-to-library" onClick={() => { setLibForm({ titulo: `Material de ${matForm.area}`, descripcion: matForm.objetivo, categoria: "actividad", url: "", contenido: matOut }); setSec("biblioteca"); }}
                    className="mt-3 btn-outline-ink rounded-full px-6 py-2.5 text-xs uppercase tracking-[0.14em]">Enviar a biblioteca</button>
                </>
              )}
            </div>
          )}

          {sec === "tienda" && (
            <div data-testid="admin-tienda" className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="label-xs">Tienda online</p>
                  <h2 className="text-3xl">Productos y ventas</h2>
                </div>
                <button data-testid="product-new" onClick={() => setProdForm({ nombre: "", descripcion: "", categoria: "Ebook", precio: "", imagen: "", archivo_url: "", activo: true })}
                  className="btn-gold flex items-center gap-2 rounded-full px-5 py-2 text-xs uppercase tracking-[0.14em]"><Plus size={13} /> Nuevo producto</button>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {products.length === 0 && <p className="card-nilo p-8 text-sm text-[#5e5a56]">Sin productos cargados.</p>}
                {products.map((p) => (
                  <div key={p.id} data-testid={`admin-product-${p.id}`} className="card-nilo overflow-hidden">
                    {p.imagen && <img src={p.imagen} alt={p.nombre} className="h-36 w-full object-cover" />}
                    <div className="p-5">
                      <span className="label-xs">{p.categoria}{p.activo ? "" : " · oculto"}</span>
                      <p className="mt-2 serif text-xl">{p.nombre}</p>
                      <p className="mt-1 text-sm text-[#5e5a56]">${Number(p.precio).toLocaleString("es-AR")}</p>
                      <div className="mt-4 flex gap-2">
                        <button data-testid={`product-edit-${p.id}`} onClick={() => setProdForm({ ...p })} className="btn-outline-ink flex items-center gap-1 rounded-full px-4 py-1.5 text-xs uppercase"><Pencil size={12} /> Editar</button>
                        <button data-testid={`product-delete-${p.id}`} onClick={() => delProduct(p.id)} className="flex items-center gap-1 rounded-full border border-[#993333] px-4 py-1.5 text-xs uppercase text-[#993333]"><Trash2 size={12} /> Eliminar</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="card-nilo p-6">
                <p className="label-xs">Ventas</p>
                <div className="mt-4 space-y-3">
                  {orders.length === 0 && <p className="text-sm text-[#5e5a56]">Sin pedidos registrados.</p>}
                  {orders.map((o) => (
                    <div key={o.id} data-testid={`order-row-${o.id}`} className="border border-[#dcd5ce] p-4 text-sm">
                      <p className="serif text-lg">{o.product_nombre}</p>
                      <p className="label-xs mt-0.5">{o.nombre} · {o.telefono || o.email || "sin contacto"} · {o.estado}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button data-testid={`order-pay-${o.id}`} onClick={() => setOrder(o.id, "pagado")} className="btn-outline-ink rounded-full px-4 py-1.5 text-xs uppercase">Marcar pagado</button>
                        <button data-testid={`order-deliver-${o.id}`} onClick={() => setOrder(o.id, "entregado")} className="btn-gold rounded-full px-4 py-1.5 text-xs uppercase">Entregar y avisar</button>
                        <button onClick={() => setOrder(o.id, "cancelado")} className="rounded-full border border-[#993333] px-4 py-1.5 text-xs uppercase text-[#993333]">Cancelar</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {sec === "actividad" && (
            <div data-testid="admin-actividad" className="card-nilo p-6">
              <p className="label-xs">Registro de actividad</p>
              <div className="mt-4 divide-y divide-[#ebe5df] text-sm">
                {acts.map((a) => (
                  <div key={a.id} className="flex justify-between py-2.5">
                    <span>{a.accion} {a.detalle && `· ${a.detalle}`}</span>
                    <span className="text-xs text-[#8b8480]">{a.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" data-testid="patient-form-modal">
          <form onSubmit={savePatient} className="max-h-[90vh] w-full max-w-2xl overflow-y-auto bg-white p-8">
            <div className="flex items-center justify-between">
              <h2 className="serif text-3xl">{form.id ? "Editar paciente" : "Nuevo paciente"}</h2>
              <button type="button" onClick={() => setForm(null)}><X size={18} /></button>
            </div>
            <div className="mt-6 grid gap-3 md:grid-cols-2">
              <input data-testid="pf-dni" required placeholder="DNI (usuario y contraseña inicial)" className={inp} value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} />
              <input data-testid="pf-nombre" required placeholder="Nombre" className={inp} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              <input data-testid="pf-apellido" required placeholder="Apellido" className={inp} value={form.apellido} onChange={(e) => setForm({ ...form, apellido: e.target.value })} />
              <input data-testid="pf-email" placeholder="Email" className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input data-testid="pf-telefono" placeholder="Teléfono" className={inp} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
              <input data-testid="pf-nac" type="date" className={inp} value={form.fecha_nacimiento} onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })} />
              <input data-testid="pf-escuela" placeholder="Escuela" className={inp} value={form.escuela} onChange={(e) => setForm({ ...form, escuela: e.target.value })} />
              <input data-testid="pf-grado" placeholder="Grado / año" className={inp} value={form.grado} onChange={(e) => setForm({ ...form, grado: e.target.value })} />
              <input data-testid="pf-tutor" placeholder="Adulto responsable" className={inp} value={form.tutor} onChange={(e) => setForm({ ...form, tutor: e.target.value })} />
              <textarea data-testid="pf-notas" rows={3} placeholder="Notas" className={`${inp} md:col-span-2`} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            </div>
            <button data-testid="pf-save" className="mt-6 btn-gold rounded-full px-8 py-3 text-xs uppercase tracking-[0.14em]">Guardar</button>
          </form>
        </div>
      )}

      {prodForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" data-testid="product-form-modal">
          <form onSubmit={saveProduct} className="max-h-[90vh] w-full max-w-lg overflow-y-auto bg-white p-8">
            <div className="flex items-center justify-between">
              <h2 className="serif text-3xl">{prodForm.id ? "Editar producto" : "Nuevo producto"}</h2>
              <button type="button" onClick={() => setProdForm(null)}><X size={18} /></button>
            </div>
            <div className="mt-6 space-y-3">
              <input data-testid="prod-nombre" required placeholder="Nombre del producto" className={inp} value={prodForm.nombre} onChange={(e) => setProdForm({ ...prodForm, nombre: e.target.value })} />
              <select data-testid="prod-categoria" className={inp} value={prodForm.categoria} onChange={(e) => setProdForm({ ...prodForm, categoria: e.target.value })}>
                {["Ebook", "Guía", "Material psicopedagógico", "Recurso educativo", "Actividad", "Cuadernillo digital"].map((c) => <option key={c}>{c}</option>)}
              </select>
              <input data-testid="prod-precio" required type="number" min="0" placeholder="Precio" className={inp} value={prodForm.precio} onChange={(e) => setProdForm({ ...prodForm, precio: e.target.value })} />
              <textarea data-testid="prod-descripcion" rows={4} placeholder="Descripción" className={inp} value={prodForm.descripcion} onChange={(e) => setProdForm({ ...prodForm, descripcion: e.target.value })} />
              <input data-testid="prod-imagen" placeholder="URL de la imagen" className={inp} value={prodForm.imagen} onChange={(e) => setProdForm({ ...prodForm, imagen: e.target.value })} />
              <input data-testid="prod-archivo" placeholder="URL del archivo digital (entrega)" className={inp} value={prodForm.archivo_url} onChange={(e) => setProdForm({ ...prodForm, archivo_url: e.target.value })} />
              <label className="flex items-center gap-2 text-xs text-[#5e5a56]">
                <input type="checkbox" data-testid="prod-activo" checked={prodForm.activo} onChange={(e) => setProdForm({ ...prodForm, activo: e.target.checked })} />
                Visible en la tienda pública
              </label>
            </div>
            <button data-testid="prod-save" className="mt-6 btn-gold rounded-full px-8 py-3 text-xs uppercase tracking-[0.14em]">Guardar producto</button>
          </form>
        </div>
      )}

      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" data-testid="delete-modal">
          <div className="w-full max-w-md bg-white p-8">
            <h2 className="serif text-2xl">Eliminar paciente</h2>
            {delStep === 1 ? (
              <>
                <p className="mt-3 text-sm text-[#5e5a56]">Se eliminarán el perfil, la historia clínica, documentos, informes, consentimientos y seguimientos de {delTarget.nombre} {delTarget.apellido}.</p>
                <div className="mt-6 flex gap-3">
                  <button data-testid="del-step1" onClick={() => setDelStep(2)} className="rounded-full bg-[#993333] px-6 py-2.5 text-xs uppercase text-white">Continuar</button>
                  <button onClick={() => { setDelTarget(null); setDelStep(0); }} className="btn-outline-ink rounded-full px-6 py-2.5 text-xs uppercase">Cancelar</button>
                </div>
              </>
            ) : (
              <>
                <p className="mt-3 text-sm text-[#5e5a56]">Confirmación final: esta acción es irreversible.</p>
                <div className="mt-6 flex gap-3">
                  <button data-testid="del-confirm" onClick={doDelete} className="rounded-full bg-[#993333] px-6 py-2.5 text-xs uppercase text-white">Eliminar definitivamente</button>
                  <button onClick={() => { setDelTarget(null); setDelStep(0); }} className="btn-outline-ink rounded-full px-6 py-2.5 text-xs uppercase">Cancelar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
